import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../App.css";
import { mainApiUrl } from "../../globalVariables";

const PROMPTS_COLLECTION = "prompts";
// Keep in sync with backend PROCESSING_TASKS_COLLECTION
const TASKS_COLLECTION = "prompts-processing-checklist-tasks";
const ORG_TEMPLATE_COLLECTION = "loan-processing-checklist-templates";

// A reference organization ID to fetch the default template structure
// This org should have no custom overrides to get the pure default templates
const REFERENCE_ORG_ID = "WeWowNPgSR0R61eWniQN"; // Flow Mortgage - used as reference

const formatIdForDisplay = (id) =>
  (id || "")
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const PromptsPage = ({ onReady }) => {
  const [activeTab, setActiveTab] = useState("prompts");
  const [isLoading, setIsLoading] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [query, setQuery] = useState("");
  const [promptDrafts, setPromptDrafts] = useState({});
  const [promptDescriptions, setPromptDescriptions] = useState({});
  const [taskDrafts, setTaskDrafts] = useState({});
  const [taskTitles, setTaskTitles] = useState({});
  const [savingIds, setSavingIds] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("prompt"); // "prompt" | "task" | "org-task"
  const [modalId, setModalId] = useState(null);
  const [modalDisplayName, setModalDisplayName] = useState("");
  const [orgId, setOrgId] = useState("");
  const [defaultTaskMap, setDefaultTaskMap] = useState({});
  const [orgUseDefaultMap, setOrgUseDefaultMap] = useState({});
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [orgSearchResults, setOrgSearchResults] = useState([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const [modalTemplateId, setModalTemplateId] = useState("");
  // Track expanded loan types for org-tasks accordion
  const [expandedLoanTypes, setExpandedLoanTypes] = useState(new Set());
  // Track expanded loan types for global Task Prompts
  const [expandedGlobalLoanTypes, setExpandedGlobalLoanTypes] = useState(new Set());
  // Store global templates structure (maps loan type -> array of {taskId, categoryTitle})
  const [globalTemplateStructure, setGlobalTemplateStructure] = useState({});
  // Selected loan type filter for Task Prompts tab
  const [selectedLoanTypeFilter, setSelectedLoanTypeFilter] = useState("");
  // Selected loan type filter for Org Checklist Prompts tab
  const [selectedOrgLoanTypeFilter, setSelectedOrgLoanTypeFilter] = useState("");
  // Filter to show only loan types with customizations
  const [showOnlyCustomized, setShowOnlyCustomized] = useState(false);
  // Store org templates from API (includes customization info)
  const [orgTemplates, setOrgTemplates] = useState([]);

  const sortedPrompts = useMemo(
    () =>
      [...prompts]
        .filter((p) => {
          const q = query.trim().toLowerCase();
          if (!q) return true;
          return (
            p.displayName?.toLowerCase().includes(q) ||
            p.id.toLowerCase().includes(q) ||
            (promptDescriptions[p.id] || "").toLowerCase().includes(q)
          );
        })
        .sort((a, b) =>
          (a.displayName || a.id).localeCompare(b.displayName || b.id)
        ),
    [prompts, query, promptDescriptions]
  );

  const sortedTasks = useMemo(
    () =>
      [...tasks]
        .filter((t) => {
          const q = query.trim().toLowerCase();
          if (!q) return true;
          return (
            (t.title || "").toLowerCase().includes(q) ||
            t.id.toLowerCase().includes(q) ||
            (taskDrafts[t.id] || "").toLowerCase().includes(q)
          );
        })
        .sort((a, b) =>
          (a.title || a.id).localeCompare(b.title || b.id)
        ),
    [tasks, query, taskDrafts]
  );

  // Toggle expansion of a loan type accordion
  const toggleLoanType = (loanType) => {
    setExpandedLoanTypes((prev) => {
      const next = new Set(prev);
      if (next.has(loanType)) {
        next.delete(loanType);
      } else {
        next.add(loanType);
      }
      return next;
    });
  };

  // Toggle expansion for global Task Prompts
  const toggleGlobalLoanType = (loanType) => {
    setExpandedGlobalLoanTypes((prev) => {
      const next = new Set(prev);
      if (next.has(loanType)) {
        next.delete(loanType);
      } else {
        next.add(loanType);
      }
      return next;
    });
  };

  // Get all available loan types for the filter dropdown
  const availableLoanTypes = useMemo(() => {
    return Object.keys(globalTemplateStructure).sort();
  }, [globalTemplateStructure]);

  // Build global tasks grouped by loan type using template structure + task prompts
  const globalTasksByLoanType = useMemo(() => {
    const grouped = {};
    const q = query.trim().toLowerCase();

    // For each loan type in the template structure
    Object.entries(globalTemplateStructure).forEach(([loanType, templateTasks]) => {
      // Apply loan type filter if selected
      if (selectedLoanTypeFilter && loanType !== selectedLoanTypeFilter) {
        return;
      }

      const tasksForLoanType = [];

      templateTasks.forEach((templateTask) => {
        // Find the actual task prompt from the tasks collection
        const taskPrompt = tasks.find((t) => t.id === templateTask.id);

        // Build the full task object
        const fullTask = {
          id: templateTask.id,
          title: taskPrompt?.title || templateTask.title || formatIdForDisplay(templateTask.id),
          instruction: taskPrompt?.instruction || "",
          categoryTitle: templateTask.categoryTitle || "Uncategorized",
          loanType,
        };

        // Apply search filter
        if (q) {
          const matchesQuery =
            fullTask.title.toLowerCase().includes(q) ||
            fullTask.id.toLowerCase().includes(q) ||
            loanType.toLowerCase().includes(q) ||
            fullTask.categoryTitle.toLowerCase().includes(q);
          if (!matchesQuery) return;
        }

        tasksForLoanType.push(fullTask);
      });

      if (tasksForLoanType.length > 0) {
        grouped[loanType] = tasksForLoanType.sort((a, b) =>
          (a.title || a.id).localeCompare(b.title || b.id)
        );
      }
    });

    return grouped;
  }, [globalTemplateStructure, tasks, query, selectedLoanTypeFilter]);

  useEffect(() => {
    const loadAll = async () => {
      setIsLoading(true);
      try {
        await Promise.all([loadPrompts(), loadTasks(), loadDefaultTaskMap()]);
      } finally {
        setIsLoading(false);
        onReady?.();
      }
    };
    loadAll();
  }, [onReady]);

  const loadPrompts = async () => {
    if (!firestoreProd) {
      toast.error("Firestore not initialized");
      return;
    }
    try {
      const snapshot = await getDocs(
        collection(firestoreProd, PROMPTS_COLLECTION)
      );
      const items = snapshot.docs.map((docRef) => {
        const data = docRef.data() || {};
        return {
          id: docRef.id,
          prompt: data.prompt || "",
          description: data.description || "",
          displayName: formatIdForDisplay(docRef.id),
        };
      });
      setPrompts(items);
      setPromptDrafts((prev) => {
        const next = { ...prev };
        items.forEach((p) => {
          if (next[p.id] === undefined) next[p.id] = p.prompt;
        });
        return next;
      });
      setPromptDescriptions((prev) => {
        const next = { ...prev };
        items.forEach((p) => {
          if (next[p.id] === undefined) next[p.id] = p.description || "";
        });
        return next;
      });
    } catch (error) {
      console.error("Failed to load prompts", error);
      toast.error("Failed to load prompts from Firestore");
    }
  };

  const loadTasks = async () => {
    if (!firestoreProd) {
      toast.error("Firestore not initialized");
      return;
    }
    try {
      const snapshot = await getDocs(
        collection(firestoreProd, TASKS_COLLECTION)
      );
      const items = snapshot.docs.map((docRef) => {
        const data = docRef.data() || {};
        return {
          id: docRef.id,
          title: data.title || formatIdForDisplay(docRef.id),
          instruction: data.instruction || "",
        };
      });
      setTasks(items);
      setTaskDrafts((prev) => {
        const next = { ...prev };
        items.forEach((t) => {
          if (next[t.id] === undefined) next[t.id] = t.instruction;
        });
        return next;
      });
      setTaskTitles((prev) => {
        const next = { ...prev };
        items.forEach((t) => {
          if (next[t.id] === undefined) next[t.id] = t.title || "";
        });
        return next;
      });

      // Also load global template structure for grouping by loan type
      await loadGlobalTemplateStructure();
    } catch (error) {
      console.error("Failed to load task prompts", error);
      toast.error("Failed to load task prompts from Firestore");
    }
  };

  // Load global template structure (loan type -> task IDs with categories)
  // Fetches from backend API using a reference organization to get default templates
  const loadGlobalTemplateStructure = async () => {
    try {
      // Fetch templates from backend API
      const apiUrl = `${mainApiUrl}/processing-checklist-templates?organizationId=${REFERENCE_ORG_ID}`;
      console.log(`[Prompts] Fetching templates from: ${apiUrl}`);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Prompts] API error response:`, errorText);
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }

      const templates = await response.json();
      console.log(`[Prompts] Received ${templates.length} templates from API`);

      if (!Array.isArray(templates) || templates.length === 0) {
        console.warn(`[Prompts] No templates returned from API`);
        return;
      }

      const structure = {};
      templates.forEach((template) => {
        const loanType = template.id;
        const checklist = template.checklist || [];

        // Extract task references from the template structure
        const templateTasks = checklist.flatMap((category) =>
          Array.isArray(category.tasks)
            ? category.tasks.map((t) => ({
                id: t.id,
                title: t.title,
                categoryTitle: category.categoryTitle || "Uncategorized",
              }))
            : []
        );

        if (templateTasks.length > 0) {
          structure[loanType] = templateTasks;
        }
      });

      console.log(`[Prompts] Global template structure loaded with ${Object.keys(structure).length} loan types:`, Object.keys(structure));
      setGlobalTemplateStructure(structure);
    } catch (error) {
      console.error("Failed to load global template structure from API", error);
      // Don't show toast here - let the UI handle showing the flat list gracefully
    }
  };

  const loadDefaultTaskMap = async () => {
    if (!firestoreProd) return;
    try {
      const snapshot = await getDocs(collection(firestoreProd, TASKS_COLLECTION));
      const map = {};
      snapshot.forEach((docRef) => {
        const data = docRef.data() || {};
        map[docRef.id] = {
          id: docRef.id,
          title: data.title || formatIdForDisplay(docRef.id),
          instruction: data.instruction || "",
        };
      });
      setDefaultTaskMap(map);
    } catch (error) {
      console.error("Failed to load default task prompts", error);
    }
  };

  const openModal = (type, item) => {
    setModalType(type);
    setModalId(item.id);
    setModalTemplateId(item.templateId || "");
    setModalDisplayName(
      type === "prompt"
        ? item.displayName || formatIdForDisplay(item.id)
        : item.title || formatIdForDisplay(item.id)
    );
    if (type === "prompt") {
      setPromptDrafts((prev) => ({ ...prev, [item.id]: item.prompt }));
      setPromptDescriptions((prev) => ({
        ...prev,
        [item.id]: item.description || "",
      }));
    } else if (type === "task") {
      setTaskDrafts((prev) => ({ ...prev, [item.id]: item.instruction }));
      setTaskTitles((prev) => ({ ...prev, [item.id]: item.title || "" }));
    } else if (type === "org-task") {
      setTaskDrafts((prev) => ({
        ...prev,
        [item.id]: item.instruction || "",
      }));
      setTaskTitles((prev) => ({ ...prev, [item.id]: item.title || "" }));
      setOrgUseDefaultMap((prev) => ({
        ...prev,
        [item.id]:
          item.useDefaultPrompt !== undefined ? item.useDefaultPrompt : true,
      }));
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalId(null);
  };

  const savePrompt = async (id) => {
    const promptValue = promptDrafts[id] ?? "";
    const description = promptDescriptions[id] ?? "";
    setSavingIds((prev) => new Set(prev).add(id));
    try {
      await setDoc(
        doc(firestoreProd, PROMPTS_COLLECTION, id),
        {
          prompt: promptValue,
          description: description || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast.success(`Saved prompt "${id}"`);
      setPrompts((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, prompt: promptValue, description } : p
        )
      );
    } catch (error) {
      console.error("Failed to save prompt", error);
      toast.error("Failed to save prompt");
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const saveTaskPrompt = async (id) => {
    const instruction = taskDrafts[id] ?? "";
    const title = taskTitles[id] ?? formatIdForDisplay(id);
    setSavingIds((prev) => new Set(prev).add(id));
    try {
      await setDoc(
        doc(firestoreProd, TASKS_COLLECTION, id),
        {
          instruction,
          title,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast.success(`Saved task prompt "${id}"`);
      setTasks((prev) =>
        prev.map((t) => (t.id === id ? { ...t, instruction, title } : t))
      );
    } catch (error) {
      console.error("Failed to save task prompt", error);
      toast.error("Failed to save task prompt");
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const tabButton = (tab, label) => (
    <button
      className={`PromptTabButton ${activeTab === tab ? "active" : ""}`}
      onClick={() => setActiveTab(tab)}
    >
      {label}
    </button>
  );

  const renderTable = (rows, type) => (
    <div className="PromptTableWrapper">
      <table className="PromptTable">
        <thead>
          <tr>
            <th style={{ width: "30%" }}>Title</th>
            <th style={{ width: "50%" }}>ID</th>
            <th style={{ width: "20%", textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{type === "prompt" ? row.displayName : row.title}</td>
              <td className="PromptIdCell">{row.id}</td>
              <td style={{ textAlign: "right" }}>
                <button
                  className="PromptActionButton"
                  onClick={() => openModal(type, row)}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="PromptEmpty">No items found for this tab.</div>
      )}
    </div>
  );

  // Expand all loan types for global tasks
  const expandAllGlobalLoanTypes = () => {
    setExpandedGlobalLoanTypes(new Set(Object.keys(globalTasksByLoanType)));
  };

  // Collapse all loan types for global tasks
  const collapseAllGlobalLoanTypes = () => {
    setExpandedGlobalLoanTypes(new Set());
  };

  // Render global Task Prompts grouped by loan type
  const renderGlobalTasksGrouped = () => {
    const loanTypes = Object.keys(globalTasksByLoanType).sort();
    const hasTemplateStructure = Object.keys(globalTemplateStructure).length > 0;

    // If no template structure loaded, show loading or fallback to flat list
    if (!hasTemplateStructure) {
      if (isLoading) {
        return <div className="PromptLoading">Loading loan types from backend...</div>;
      }
      // Show info banner with retry button and fallback to flat list
      return (
        <div className="TaskPromptsContainer">
          <div className="LoanTypeInfoBanner">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <strong>Note:</strong> Could not load loan type templates from the backend API. 
                Showing all {sortedTasks.length} task prompts in a flat list. Check the console for error details.
              </div>
              <button
                className="PromptSecondaryButton"
                onClick={() => {
                  setIsLoading(true);
                  loadGlobalTemplateStructure().finally(() => setIsLoading(false));
                }}
              >
                Retry Loading Templates
              </button>
            </div>
          </div>
          {renderTable(sortedTasks, "task")}
        </div>
      );
    }

    // Count total tasks across all visible loan types
    const totalVisibleTasks = loanTypes.reduce(
      (sum, lt) => sum + (globalTasksByLoanType[lt]?.length || 0),
      0
    );

    return (
      <div className="TaskPromptsContainer">
        {/* Loan Type Filter Controls */}
        <div className="LoanTypeFilterControls">
          <div className="LoanTypeFilterRow">
            <label className="LoanTypeFilterLabel">Filter by Loan Type:</label>
            <select
              className="LoanTypeSelect"
              value={selectedLoanTypeFilter}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedLoanTypeFilter(value);
                if (value) {
                  // Auto-expand the selected loan type
                  setExpandedGlobalLoanTypes(new Set([value]));
                }
              }}
            >
              <option value="">All Loan Types ({availableLoanTypes.length})</option>
              {availableLoanTypes.map((lt) => (
                <option key={lt} value={lt}>
                  {formatIdForDisplay(lt)} ({globalTemplateStructure[lt]?.length || 0} tasks)
                </option>
              ))}
            </select>
            {selectedLoanTypeFilter && (
              <button
                className="PromptSecondaryButton LoanTypeClearButton"
                onClick={() => {
                  setSelectedLoanTypeFilter("");
                  setExpandedGlobalLoanTypes(new Set());
                }}
              >
                Clear Filter
              </button>
            )}
          </div>
          <div className="LoanTypeFilterActions">
            <button
              className="PromptSecondaryButton"
              onClick={expandAllGlobalLoanTypes}
              disabled={loanTypes.length === 0}
            >
              Expand All
            </button>
            <button
              className="PromptSecondaryButton"
              onClick={collapseAllGlobalLoanTypes}
              disabled={expandedGlobalLoanTypes.size === 0}
            >
              Collapse All
            </button>
            <span className="LoanTypeStats">
              {loanTypes.length} loan type{loanTypes.length !== 1 ? "s" : ""} · {totalVisibleTasks} task{totalVisibleTasks !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {loanTypes.length === 0 ? (
          <div className="PromptEmpty">
            No tasks match your search{selectedLoanTypeFilter ? ` in "${formatIdForDisplay(selectedLoanTypeFilter)}"` : ""}.
          </div>
        ) : (
          <div className="LoanTypeAccordionList">
            {loanTypes.map((loanType) => {
              const loanTypeTasks = globalTasksByLoanType[loanType] || [];
              const isExpanded = expandedGlobalLoanTypes.has(loanType);

              return (
                <div key={loanType} className="LoanTypeAccordion">
                  <button
                    className="LoanTypeAccordionHeader"
                    onClick={() => toggleGlobalLoanType(loanType)}
                  >
                    <span className="LoanTypeAccordionIcon">
                      {isExpanded ? "▼" : "▶"}
                    </span>
                    <span className="LoanTypeAccordionTitle">
                      {formatIdForDisplay(loanType)}
                    </span>
                    <span className="LoanTypeAccordionCount">
                      {loanTypeTasks.length} task{loanTypeTasks.length !== 1 ? "s" : ""}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="LoanTypeAccordionContent">
                      <table className="PromptTable">
                        <thead>
                          <tr>
                            <th style={{ width: "25%" }}>Title</th>
                            <th style={{ width: "35%" }}>ID</th>
                            <th style={{ width: "25%" }}>Category</th>
                            <th style={{ width: "15%", textAlign: "right" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loanTypeTasks.map((t) => (
                            <tr key={`${loanType}-${t.id}`}>
                              <td>{t.title}</td>
                              <td className="PromptIdCell">{t.id}</td>
                              <td>{t.categoryTitle || "—"}</td>
                              <td style={{ textAlign: "right" }}>
                                <button
                                  className="PromptActionButton"
                                  onClick={() => openModal("task", t)}
                                >
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderModal = () => {
    if (!modalOpen || !modalId) return null;
    const isPrompt = modalType === "prompt";
    const isOrg = modalType === "org-task";
    const saving = savingIds.has(modalId);
    const currentPrompt = promptDrafts[modalId] || "";
    const currentDescription = promptDescriptions[modalId] || "";
    const currentTaskInstruction = taskDrafts[modalId] || "";
    const currentTaskTitle = taskTitles[modalId] || modalDisplayName;
    const defaultTask = defaultTaskMap[modalId];
    const currentOrgUseDefault = orgUseDefaultMap[modalId] ?? true;
    const orgInstruction = taskDrafts[modalId] || "";

    return (
      <div className="PromptModalOverlay" onClick={closeModal}>
        <div
          className="PromptModal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="PromptModalHeader">
            <div>
              <div className="PromptTitle">{modalDisplayName}</div>
              <div className="PromptId">{modalId}</div>
            </div>
            <button className="PromptCloseButton" onClick={closeModal}>
              ×
            </button>
          </div>

          {isPrompt ? (
            <div className="PromptModalBody">
              <div className="PromptField">
                <label>Description</label>
                <input
                  value={currentDescription}
                  onChange={(e) =>
                    setPromptDescriptions((prev) => ({
                      ...prev,
                      [modalId]: e.target.value,
                    }))
                  }
                  className="PromptInput"
                  placeholder="Description (optional)"
                />
              </div>
              <div className="PromptField PromptFieldFill">
                <label>Prompt</label>
                <textarea
                  className="PromptTextarea"
                  value={currentPrompt}
                  onChange={(e) =>
                    setPromptDrafts((prev) => ({
                      ...prev,
                      [modalId]: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          ) : (
            <>
              <div className="PromptField">
                <label>Title</label>
                <input
                  value={currentTaskTitle}
                  onChange={(e) =>
                    setTaskTitles((prev) => ({
                      ...prev,
                      [modalId]: e.target.value,
                    }))
                  }
                  className="PromptInput"
                  placeholder="Title"
                />
              </div>
              {isOrg ? (
                <>
                  <div className="PromptField PromptInline">
                    <label>Use default prompt</label>
                    <input
                      type="checkbox"
                      checked={currentOrgUseDefault}
                      onChange={(e) =>
                        setOrgUseDefaultMap((prev) => ({
                          ...prev,
                          [modalId]: e.target.checked,
                        }))
                      }
                    />
                  </div>
                  {!currentOrgUseDefault && (
                    <div className="PromptField">
                      <label>Organization Override</label>
                      <textarea
                        className="PromptTextarea"
                        value={orgInstruction}
                        onChange={(e) =>
                          setTaskDrafts((prev) => ({
                            ...prev,
                            [modalId]: e.target.value,
                          }))
                        }
                        rows={12}
                      />
                    </div>
                  )}
                  <div className="PromptField">
                    <label>Default Prompt (read-only)</label>
                    <textarea
                      className="PromptTextarea PromptTextareaReadOnly"
                      value={defaultTask?.instruction || ""}
                      readOnly
                      rows={8}
                    />
                  </div>
                </>
              ) : (
                <div className="PromptField">
                  <label>Instruction</label>
                  <textarea
                    className="PromptTextarea"
                    value={currentTaskInstruction}
                    onChange={(e) =>
                      setTaskDrafts((prev) => ({
                        ...prev,
                        [modalId]: e.target.value,
                      }))
                    }
                    rows={12}
                  />
                </div>
              )}
            </>
          )}

          <div className="PromptModalActions">
            <button className="PromptSecondaryButton" onClick={closeModal}>
              Cancel
            </button>
            <button
              className="PromptSaveButton"
              onClick={() => {
                if (isPrompt) return savePrompt(modalId);
                if (isOrg) return saveOrgTaskPrompt(modalId);
                return saveTaskPrompt(modalId);
              }}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Loading state for org templates
  const [orgTemplatesLoading, setOrgTemplatesLoading] = useState(false);

  const loadOrgTemplate = async (orgToLoad) => {
    const targetOrgId = orgToLoad || orgId;
    if (!targetOrgId) return;
    
    setOrgTemplatesLoading(true);
    setOrgId(targetOrgId); // Set org ID immediately so UI reflects selection
    
    try {
      // Fetch all templates for this org from the API
      const apiUrl = `${mainApiUrl}/processing-checklist-templates?organizationId=${targetOrgId}`;
      console.log(`[Prompts] Fetching org templates from: ${apiUrl}`);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }

      const templates = await response.json();
      console.log(`[Prompts] Received ${templates.length} templates for org ${targetOrgId}`);

      // Also load org-specific overrides from Firestore to detect customizations
      let orgOverrides = {};
      if (firestoreProd) {
        const templatesRef = collection(
          firestoreProd,
          "organizations",
          targetOrgId,
          ORG_TEMPLATE_COLLECTION
        );
        const orgDocs = await getDocs(templatesRef);
        orgDocs.docs.forEach((d) => {
          orgOverrides[d.id] = d.data();
        });
      }

      // Store templates with customization info
      const templatesWithInfo = templates.map((template) => {
        const hasOrgOverride = !!orgOverrides[template.id];
        const checklist = template.checklist || [];
        
        // Count customized tasks (tasks where useDefaultPrompt is false)
        let customizedTaskCount = 0;
        const tasksArray = checklist.flatMap((category) => {
          if (!Array.isArray(category.tasks)) return [];
          return category.tasks.map((t) => {
            const isCustomized = t.useDefaultPrompt === false || t.lastUpdatedByAdmin === true;
            if (isCustomized) customizedTaskCount++;
            return {
              ...t,
              templateId: template.id,
              categoryTitle: category.categoryTitle || "Uncategorized",
              isCustomized,
            };
          });
        });

        return {
          id: template.id,
          name: template.name,
          hasOrgOverride,
          customizedTaskCount,
          totalTaskCount: tasksArray.length,
          tasks: tasksArray,
        };
      });

      setOrgTemplates(templatesWithInfo);

      // Build useDefaultMap from all tasks across all templates
      const useDefaultMap = {};
      templatesWithInfo.forEach((template) => {
        template.tasks.forEach((t) => {
          useDefaultMap[t.id] =
            t.useDefaultPrompt !== undefined ? t.useDefaultPrompt : true;
        });
      });
      setOrgUseDefaultMap(useDefaultMap);

      toast.success(`Loaded ${templatesWithInfo.length} loan types for organization`);
    } catch (error) {
      console.error("Failed to load org templates", error);
      toast.error(`Failed to load org templates: ${error.message}`);
      setOrgTemplates([]);
    } finally {
      setOrgTemplatesLoading(false);
    }
  };

  const saveOrgTaskPrompt = async (id) => {
    const resolvedTemplateId = modalTemplateId;
    if (!orgId || !resolvedTemplateId) {
      toast.error("Set org and template first");
      return;
    }
    const useDefault = orgUseDefaultMap[id] ?? true;
    const instruction = taskDrafts[id] ?? "";
    const title = taskTitles[id] ?? formatIdForDisplay(id);

    const templateRef = doc(
      firestoreProd,
      "organizations",
      orgId,
      ORG_TEMPLATE_COLLECTION,
      resolvedTemplateId
    );

    try {
      const snapshot = await getDoc(templateRef);
      if (!snapshot.exists()) {
        toast.error("Template not found for this org");
        return;
      }
      const data = snapshot.data() || {};
      const templates =
        Array.isArray(data.loanProcessingChecklistTemplate) &&
        data.loanProcessingChecklistTemplate.length
          ? [...data.loanProcessingChecklistTemplate]
          : [];

      let updated = false;
      const updatedTemplates = templates.map((category) => {
        if (!Array.isArray(category.tasks)) return category;
        const newTasks = category.tasks.map((t) => {
          if (t.id !== id) return t;
          updated = true;
          return {
            ...t,
            title,
            instruction: useDefault
              ? defaultTaskMap[id]?.instruction || t.instruction
              : instruction,
            useDefaultPrompt: useDefault,
            lastUpdatedByAdmin: true,
          };
        });
        return { ...category, tasks: newTasks };
      });

      if (!updated) {
        toast.error("Task not found in template");
        return;
      }

      await updateDoc(templateRef, {
        loanProcessingChecklistTemplate: updatedTemplates,
        updatedAt: serverTimestamp(),
      });

      toast.success("Saved org task prompt");
      // Update the task in orgTemplates
      setOrgTemplates((prev) =>
        prev.map((template) => {
          if (template.id !== resolvedTemplateId) return template;
          const updatedTasks = template.tasks.map((t) =>
            t.id === id
              ? {
                  ...t,
                  title,
                  instruction: useDefault
                    ? defaultTaskMap[id]?.instruction || t.instruction
                    : instruction,
                  useDefaultPrompt: useDefault,
                  isCustomized: !useDefault,
                }
              : t
          );
          const customizedTaskCount = updatedTasks.filter((t) => t.isCustomized).length;
          return {
            ...template,
            tasks: updatedTasks,
            customizedTaskCount,
          };
        })
      );
    } catch (error) {
      console.error("Failed to save org task prompt", error);
      toast.error("Failed to save org task prompt");
    }
  };

  // Cache for orgs with defaultForUserId to avoid repeated fetches
  const [cachedOrgs, setCachedOrgs] = useState(null);
  const [lastCacheTime, setLastCacheTime] = useState(0);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const loadOrgsWithDefaultUser = async () => {
    // Check if we have a valid cache
    const now = Date.now();
    if (cachedOrgs && (now - lastCacheTime) < CACHE_DURATION) {
      return cachedOrgs;
    }

    // Load all organizations (not just those with defaultForUserId)
    // because some orgs might have names/orgName but not defaultForUserId
    const orgsRef = collection(firestoreProd, "organizations");
    const snap = await getDocs(orgsRef);
    
    const orgs = snap.docs.map((d) => {
      const data = d.data() || {};
      return {
        id: d.id,
        name: data.name || data.orgName || "",
        defaultForUserId: data.defaultForUserId || "",
        createdAt: data.createdAt,
      };
    });

    // Sort by name for better UX (orgs with names first, then by name)
    orgs.sort((a, b) => {
      // Prioritize orgs with names
      if (a.name && !b.name) return -1;
      if (!a.name && b.name) return 1;
      return (a.name || a.id).localeCompare(b.name || b.id);
    });

    console.log(`[Prompts] Cached ${orgs.length} organizations`);
    setCachedOrgs(orgs);
    setLastCacheTime(now);
    return orgs;
  };

  // Clear the selected org and reset state
  const clearSelectedOrg = () => {
    setOrgId("");
    setOrgTemplates([]);
    setOrgSearchResults([]);
    setOrgSearchQuery("");
    setSelectedOrgLoanTypeFilter("");
    setShowOnlyCustomized(false);
    setExpandedLoanTypes(new Set());
  };

  const searchOrganizationsByName = async () => {
    if (!firestoreProd) {
      setOrgSearchResults([]);
      return;
    }

    const searchTerm = orgSearchQuery.trim().toLowerCase();
    
    // If empty search, show recently used or first few orgs
    if (!searchTerm) {
      setOrgSearchLoading(true);
      try {
        const orgs = await loadOrgsWithDefaultUser();
        // Show first 10 orgs as suggestions
        setOrgSearchResults(orgs.slice(0, 10));
      } catch (error) {
        console.error("Failed to load organizations", error);
      } finally {
        setOrgSearchLoading(false);
      }
      return;
    }

    setOrgSearchLoading(true);
    try {
      // First, check if it looks like an org ID (exact match attempt)
      if (searchTerm.length >= 10 && !searchTerm.includes(" ")) {
        // Try direct document lookup for potential org ID
        const directRef = doc(firestoreProd, "organizations", orgSearchQuery.trim());
        const directSnap = await getDoc(directRef);
        if (directSnap.exists()) {
          const data = directSnap.data() || {};
          const result = {
            id: directSnap.id,
            name: data.name || data.orgName || "",
            defaultForUserId: data.defaultForUserId || "",
          };
          setOrgSearchResults([result]);
          // Auto-load if exact match
          await loadOrgTemplate(result.id);
          setOrgSearchLoading(false);
          return;
        }
      }

      // Use cached orgs for faster search
      const orgs = await loadOrgsWithDefaultUser();
      
      // Filter cached orgs by search term
      const results = orgs
        .filter((org) => {
          const name = (org.name || "").toLowerCase();
          const email = (org.defaultForUserId || "").toLowerCase();
          const id = org.id.toLowerCase();
          return (
            name.includes(searchTerm) ||
            email.includes(searchTerm) ||
            id.includes(searchTerm)
          );
        })
        .slice(0, 20);

      setOrgSearchResults(results);
      
      // Auto-load if only one result
      if (results.length === 1) {
        await loadOrgTemplate(results[0].id);
      }
    } catch (error) {
      console.error("Failed to search organizations", error);
      toast.error("Failed to search organizations");
    } finally {
      setOrgSearchLoading(false);
    }
  };

  return (
    <div className="PromptsPage">
      <ToastContainer position="bottom-right" />
      <div className="PromptTopBar">
        <div className="PromptTabs">
          {tabButton("prompts", "Prompts")}
          {tabButton("tasks", "Task Prompts")}
          {tabButton("org-tasks", "Org Checklist Prompts")}
        </div>
        <input
          className="PromptSearch"
          placeholder="Search by title, id, or content"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="PromptLoading">Loading...</div>
      ) : activeTab === "prompts" ? (
        renderTable(sortedPrompts, "prompt")
      ) : activeTab === "tasks" ? (
        renderGlobalTasksGrouped()
      ) : (
        <div className="TaskPromptsContainer">
          {/* Org Search Controls */}
          <div className="LoanTypeFilterControls">
            <div className="LoanTypeFilterRow">
              <label className="LoanTypeFilterLabel">Organization:</label>
              {orgId ? (
                // Show selected org with clear button
                <div className="SelectedOrgDisplay">
                  <span className="SelectedOrgName" title={`ID: ${orgId}`}>
                    {cachedOrgs?.find(o => o.id === orgId)?.name || orgId}
                  </span>
                  <span className="SelectedOrgId">({orgId.slice(0, 12)}...)</span>
                  <button
                    className="SelectedOrgClearButton"
                    onClick={clearSelectedOrg}
                    title="Clear and select a different organization"
                  >
                    ✕ Change Org
                  </button>
                </div>
              ) : (
                // Show search input
                <>
                  <input
                    className="PromptInput"
                    style={{ flex: 1, maxWidth: 350 }}
                    placeholder="Search by org name, user email, or org ID..."
                    value={orgSearchQuery}
                    onChange={(e) => setOrgSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchOrganizationsByName()}
                  />
                  <button
                    className="PromptSecondaryButton"
                    onClick={searchOrganizationsByName}
                    disabled={orgSearchLoading}
                  >
                    {orgSearchLoading ? "Searching..." : orgSearchQuery.trim() ? "Search" : "Show All"}
                  </button>
                </>
              )}
            </div>
            {!orgId && cachedOrgs && (
              <div className="OrgCacheInfo">
                {cachedOrgs.length} organizations cached · 
                <button 
                  className="OrgCacheRefreshButton"
                  onClick={() => { setCachedOrgs(null); setLastCacheTime(0); searchOrganizationsByName(); }}
                >
                  Refresh
                </button>
              </div>
            )}
          </div>

          {/* Org Search Results - only show when no org is selected */}
          {!orgId && orgSearchResults.length > 0 && (
            <div className="OrgSearchResultsContainer">
              <div className="OrgSearchResultsHeader">
                {orgSearchQuery.trim() 
                  ? `Found ${orgSearchResults.length} organization${orgSearchResults.length !== 1 ? 's' : ''}`
                  : `Showing ${orgSearchResults.length} organizations`
                }
              </div>
              <div className="OrgSearchResults">
                {orgSearchResults.map((org) => (
                  <button
                    key={org.id}
                    className={`OrgSearchResultButton ${org.id === orgId ? 'active' : ''}`}
                    onClick={() => loadOrgTemplate(org.id)}
                    title={`ID: ${org.id}\nUser: ${org.defaultForUserId || 'N/A'}`}
                  >
                    <span className="OrgSearchResultName">{org.name || org.id}</span>
                    {org.defaultForUserId && (
                      <span className="OrgSearchResultEmail">{org.defaultForUserId}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Show loan type controls once an org is loaded */}
          {orgTemplates.length > 0 && (
            <>
              {/* Loan Type Filter Controls */}
              <div className="LoanTypeFilterControls">
                <div className="LoanTypeFilterRow">
                  <label className="LoanTypeFilterLabel">Filter by Loan Type:</label>
                  <select
                    className="LoanTypeSelect"
                    value={selectedOrgLoanTypeFilter}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedOrgLoanTypeFilter(value);
                      if (value) {
                        setExpandedLoanTypes(new Set([value]));
                      }
                    }}
                  >
                    <option value="">All Loan Types ({orgTemplates.length})</option>
                    {orgTemplates
                      .filter((t) => !showOnlyCustomized || t.customizedTaskCount > 0)
                      .sort((a, b) => a.id.localeCompare(b.id))
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {formatIdForDisplay(t.id)} ({t.totalTaskCount} tasks{t.customizedTaskCount > 0 ? `, ${t.customizedTaskCount} customized` : ''})
                        </option>
                      ))}
                  </select>
                  {selectedOrgLoanTypeFilter && (
                    <button
                      className="PromptSecondaryButton LoanTypeClearButton"
                      onClick={() => {
                        setSelectedOrgLoanTypeFilter("");
                        setExpandedLoanTypes(new Set());
                      }}
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
                <div className="LoanTypeFilterActions">
                  <label className="LoanTypeCheckboxLabel">
                    <input
                      type="checkbox"
                      checked={showOnlyCustomized}
                      onChange={(e) => setShowOnlyCustomized(e.target.checked)}
                    />
                    Show only customized loan types
                  </label>
                  <button
                    className="PromptSecondaryButton"
                    onClick={() => setExpandedLoanTypes(new Set(orgTemplates.map((t) => t.id)))}
                  >
                    Expand All
                  </button>
                  <button
                    className="PromptSecondaryButton"
                    onClick={() => setExpandedLoanTypes(new Set())}
                    disabled={expandedLoanTypes.size === 0}
                  >
                    Collapse All
                  </button>
                  <span className="LoanTypeStats">
                    {orgTemplates.filter((t) => t.customizedTaskCount > 0).length} of {orgTemplates.length} loan types have customizations
                  </span>
                </div>
              </div>

              {/* Loan Type Accordion List */}
              <div className="LoanTypeAccordionList">
                {orgTemplates
                  .filter((template) => {
                    // Apply loan type filter
                    if (selectedOrgLoanTypeFilter && template.id !== selectedOrgLoanTypeFilter) {
                      return false;
                    }
                    // Apply customized filter
                    if (showOnlyCustomized && template.customizedTaskCount === 0) {
                      return false;
                    }
                    // Apply search query
                    const q = query.trim().toLowerCase();
                    if (q) {
                      const matchesLoanType = template.id.toLowerCase().includes(q) || 
                        template.name?.toLowerCase().includes(q);
                      const matchesTask = template.tasks.some((t) => 
                        t.title?.toLowerCase().includes(q) || t.id.toLowerCase().includes(q)
                      );
                      return matchesLoanType || matchesTask;
                    }
                    return true;
                  })
                  .sort((a, b) => {
                    // Sort customized loan types first
                    if (a.customizedTaskCount > 0 && b.customizedTaskCount === 0) return -1;
                    if (a.customizedTaskCount === 0 && b.customizedTaskCount > 0) return 1;
                    return a.id.localeCompare(b.id);
                  })
                  .map((template) => {
                    const isExpanded = expandedLoanTypes.has(template.id);
                    const hasCustomizations = template.customizedTaskCount > 0;
                    
                    // Filter tasks by search query
                    const q = query.trim().toLowerCase();
                    const filteredTasks = q
                      ? template.tasks.filter((t) => 
                          t.title?.toLowerCase().includes(q) || t.id.toLowerCase().includes(q)
                        )
                      : template.tasks;

                    return (
                      <div key={template.id} className={`LoanTypeAccordion ${hasCustomizations ? 'has-customizations' : ''}`}>
                        <button
                          className="LoanTypeAccordionHeader"
                          onClick={() => toggleLoanType(template.id)}
                        >
                          <span className="LoanTypeAccordionIcon">
                            {isExpanded ? "▼" : "▶"}
                          </span>
                          <span className="LoanTypeAccordionTitle">
                            {formatIdForDisplay(template.id)}
                          </span>
                          {hasCustomizations && (
                            <span className="LoanTypeCustomizedBadge">
                              {template.customizedTaskCount} customized
                            </span>
                          )}
                          <span className="LoanTypeAccordionCount">
                            {template.totalTaskCount} task{template.totalTaskCount !== 1 ? "s" : ""}
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="LoanTypeAccordionContent">
                            <table className="PromptTable">
                              <thead>
                                <tr>
                                  <th style={{ width: "30%" }}>Title</th>
                                  <th style={{ width: "30%" }}>ID</th>
                                  <th style={{ width: "15%" }}>Category</th>
                                  <th style={{ width: "10%" }}>Status</th>
                                  <th style={{ width: "15%", textAlign: "right" }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredTasks.map((t) => {
                                  const isDefault =
                                    t.useDefaultPrompt !== undefined
                                      ? t.useDefaultPrompt
                                      : true;
                                  const status = isDefault ? "Default" : "Customized";
                                  return (
                                    <tr key={`${template.id}-${t.id}`} className={!isDefault ? 'customized-row' : ''}>
                                      <td>{t.title || formatIdForDisplay(t.id)}</td>
                                      <td className="PromptIdCell">{t.id}</td>
                                      <td>{t.categoryTitle || "—"}</td>
                                      <td>
                                        <span className={`StatusBadge ${isDefault ? 'default' : 'customized'}`}>
                                          {status}
                                        </span>
                                      </td>
                                      <td style={{ textAlign: "right" }}>
                                        <button
                                          className="PromptActionButton"
                                          onClick={() => {
                                            setTaskDrafts((prev) => ({
                                              ...prev,
                                              [t.id]: t.instruction || "",
                                            }));
                                            setTaskTitles((prev) => ({
                                              ...prev,
                                              [t.id]: t.title || "",
                                            }));
                                            setOrgUseDefaultMap((prev) => ({
                                              ...prev,
                                              [t.id]:
                                                t.useDefaultPrompt !== undefined
                                                  ? t.useDefaultPrompt
                                                  : true,
                                            }));
                                            openModal("org-task", t);
                                          }}
                                        >
                                          Edit
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </>
          )}

          {/* Loading state when fetching org templates */}
          {orgTemplatesLoading && (
            <div className="PromptLoading">
              Loading loan types for organization...
            </div>
          )}

          {/* Empty state when no org is loaded */}
          {!orgTemplatesLoading && orgTemplates.length === 0 && !orgId && (
            <div className="PromptEmpty">
              Search for and select an organization above to view its loan types and task customizations.
            </div>
          )}

          {/* Empty state when org is loaded but no templates (and not loading) */}
          {!orgTemplatesLoading && orgId && orgTemplates.length === 0 && (
            <div className="PromptEmpty">
              No templates found for organization <strong>{orgId}</strong>. 
              Check the browser console for error details.
            </div>
          )}
        </div>
      )}
      {renderModal()}
    </div>
  );
};

export default PromptsPage;
